<template>
  <div class="!pt-0">
    <div class="flex flex-col max-h-screen pt-16 pb-2.5 min-h-screen space-y-2.5">
      <div class="element-island m-auto mt-2.5 p-4">
        <h1 class="font-semibold text-3xl">
          {{ $t('Guest.DelayProvisionShow.title') }}
        </h1>

        <div
          v-if="delay"
          class="flex flex-col space-y-4 mt-4 text-lg"
        >
          <div
            v-if="delay.status === DelayStatus.provisioned"
            class="p-4 rounded-lg mx-auto"
            :style="{ backgroundColor: delay.statusColour, color: delay.statusTextColour }"
          >
            {{ $t('Guest.DelayProvisionShow.approved') }}
          </div>

          <div>
            <span class="font-semibold">
              {{ $t('Guest.DelayProvisionShow.delayDetails') }}
            </span>

            <p>
              {{ delay.code }} - {{ delay.description }}
            </p>
          </div>

          <div class="flex justify-between font-semibold">
            <span>
              {{ $t('Guest.DelayProvisionShow.delayDuration') }}
            </span>

            <span>
              {{ delay.days }}
            </span>
          </div>

          <button
            type="button"
            class="btn btn--primary-blue w-full disabled:cursor-not-allowed"
            :disabled="delay.status === DelayStatus.provisioned"
            @click="provisionDelay"
          >
            {{ $t('Guest.DelayProvisionShow.approveDelay') }}
          </button>

          <button
            type="button"
            class="btn btn--primary-blue w-full disabled:cursor-not-allowed"
            :disabled="delay.status === DelayStatus.provisioned"
            @click="declineSubmission"
          >
            {{ $t('Guest.DelayProvisionShow.declineDelayDuration') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useSignedData } from '@/composables/useSignedData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { parseUrlParams } from '@/helpers';
import { t } from '@/i18n';
import { DelayStatus, DelayStatusColours } from '@/models/Delay';
import { useDelaysStore } from '@/store/delays';
import Swal from 'sweetalert2';
import { computed } from 'vue';

const props = defineProps<{
  delayId: string;
}>();

const signedParams = useSignedData();
const delaysStore = useDelaysStore();

delaysStore.models.save(signedParams.models.delay);

const delay = computed(() => {
  return delaysStore.models.find(parseInt(props.delayId));
});

const provisionDelayAction = useStoreApiAction(delaysStore.provisionDelay);
const declineSubmissionAction = useStoreApiAction(delaysStore.declineSubmission);

const provisionDelayParams = computed(() => {
  return parseUrlParams(signedParams.meta.provision_delay_url as string);
});

const declineSubmissionDelayParams = computed(() => {
  return parseUrlParams(signedParams.meta.decline_submission_url as string);
});

const provisionDelay = () => {
  Swal.fire({
    icon: 'question',
    title: t('Guest.DelayProvisionShow.approveDelayQuestion'),
    text: t('Guest.DelayProvisionShow.approveDelayConfirmation'),
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      provisionDelayAction.request(props.delayId, provisionDelayParams.value).then(
        () => {
          Swal.fire({
            icon: 'success',
            title: t('Guest.DelayProvisionShow.delayApproved'),
            text: t('Guest.DelayProvisionShow.closeWindowMessage'),
          });
        },
      ).catch((error) => {
        Swal.fire({
          icon: 'error',
          title: t('Guest.DelayProvisionShow.approveDelayFailed'),
          text: error?.data?.message,
        });
      });
    }
  });
};

const declineSubmission = () => {
  Swal.fire({
    icon: 'question',
    title: t('Guest.DelayProvisionShow.declineDurationQuestion'),
    showCancelButton: true,
    inputLabel: t('Guest.DelayProvisionShow.reasonRequired'),
    input: 'textarea',
  }).then((result) => {
    if(result.isConfirmed) {
      declineSubmissionAction.request(props.delayId, declineSubmissionDelayParams.value, { reason: result.value }).then(
        () => {
          Swal.fire({
            icon: 'success',
            title: t('Guest.DelayProvisionShow.delayDurationDeclined'),
            text: t('Guest.DelayProvisionShow.closeWindowMessage'),
          });
        },
      ).catch((error) => {
        Swal.fire({
          icon: 'error',
          title: t('Guest.DelayProvisionShow.declineFailed'),
          text: error?.data?.message,
        });
      });
    }
  });
};
</script>

<style scoped></style>
